import { ArrowRight, CalendarCheck2, Check } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { trackProductEvent } from "../analytics";
import Seo from "../components/Seo";
import { useAuth } from "../context/AuthContext";
import { apiErrorMessage } from "../utils/apiError";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const plan = searchParams.get("plan") ?? "free";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    trackProductEvent("registration_started");
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await register({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
        accepted_terms: accepted,
      });
      trackProductEvent("registration_completed");
      navigate(`/onboarding?plan=${encodeURIComponent(plan)}`);
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(
          requestError,
          "Registreren is niet gelukt. Controleer de ingevulde gegevens.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="auth-page">
      <Seo
        title="Registreren"
        path="/registreren"
        description="Maak gratis je persoonlijke DeVerlofkaart-account aan."
        noIndex
      />
      <div className="auth-shell register-shell">
        <div className="auth-message">
          <span>
            <CalendarCheck2 size={22} />
          </span>
          <p className="eyebrow">Gratis starten</p>
          <h1>Van losse uren naar één helder verlofjaar.</h1>
          <ul>
            <li>
              <Check size={17} /> Geen creditcard voor persoonlijk gebruik
            </li>
            <li>
              <Check size={17} /> Werkpatroon en saldi in enkele stappen
            </li>
            <li>
              <Check size={17} /> Later zonder dataverlies upgraden
            </li>
          </ul>
        </div>
        <form className="auth-form surface" onSubmit={onSubmit}>
          <div>
            <h2>Account aanmaken</h2>
            <p>Daarna richten we je verlofkaart samen in.</p>
          </div>
          <div className="form-row">
            <label>
              <span>Voornaam</span>
              <input
                autoComplete="given-name"
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                required
              />
            </label>
            <label>
              <span>Achternaam</span>
              <input
                autoComplete="family-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                required
              />
            </label>
          </div>
          <label>
            <span>E-mailadres</span>
            <input
              autoComplete="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
          <label>
            <span>Wachtwoord</span>
            <input
              autoComplete="new-password"
              type="password"
              minLength={10}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
            <small>Minimaal 10 tekens.</small>
          </label>
          <label className="check-field">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(event) => setAccepted(event.target.checked)}
              required
            />
            <span>
              Ik ga akkoord met de{" "}
              <Link to="/algemene-voorwaarden">Algemene voorwaarden</Link> en heb de{" "}
              <Link to="/privacy">Privacyverklaring</Link> gelezen.
            </span>
          </label>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button
            className="button primary full-width"
            type="submit"
            disabled={submitting || !accepted}
          >
            {submitting ? "Account maken..." : "Account aanmaken"}
            <ArrowRight size={17} />
          </button>
          <p className="auth-footnote">
            Al een account? <Link to="/inloggen">Inloggen</Link>
          </p>
        </form>
      </div>
    </section>
  );
}
